import { EntityType, useGetExamCourseComments } from '@exam-room/graphql/preview';
import { useTranslation } from '@exam-room/translations';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { CardSkeleton } from '../../../../components';
import { CommentRichTextView, EmptyReviewState } from '../../components';
import { CommentForm } from '../../containers/comment-form';

interface CommentSectionProps {
  questionId: string;
  taskId: string;
  isReadonly?: boolean;
}

export default function CommentSection(props: CommentSectionProps) {
  const { questionId, taskId, isReadonly = false } = props;

  const { t } = useTranslation();

  const { data: examCourseComment, loading: isCommentsLoading } = useGetExamCourseComments({
    variables: {
      getExamCourseCommentsInput: {
        entityId: questionId,
        entityType: EntityType.Question,
        taskId,
      },
    },
  });

  const comments = examCourseComment?.excGetExamCourseComments.comments ?? [];

  const sortedComments = useMemo(
    () =>
      [...comments].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [comments],
  );

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='flex w-full flex-col items-start gap-2'>
        <h3 className='text-text-primary text-xl font-semibold'>
          {t('examPreview.reviewResultSection.comment')}
        </h3>
        {isReadonly ? null : <CommentForm questionId={questionId} taskId={taskId} />}
      </div>
      {isCommentsLoading && comments.length === 0 ? <CardSkeleton className='h-13 w-full' /> : null}
      {isReadonly && !isCommentsLoading && comments.length === 0 ? (
        <EmptyReviewState message={t('examPreview.reviewResultSection.emptyComment')} />
      ) : null}
      {comments.length > 0 ? (
        <div className='mt-2 flex w-full flex-col gap-4'>
          {sortedComments.map((comment) => {
            const name = `${comment.user?.firstname ?? ''} ${comment.user?.lastname ?? ''}`.trim();
            const createdAtText = comment.createdAt
              ? dayjs(comment.createdAt).format('DD-MM-YYYY, HH:mm')
              : '';
            return (
              <div key={comment.id} className='bg-grey-100 rte-root rounded-lg p-4'>
                <div className='rte-root mb-2 flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <span className='text-text-primary text-sm font-semibold'>
                      {name ? name : 'Unknown user'}
                    </span>
                    <span className='text-xs text-gray-500'>{createdAtText}</span>
                  </div>
                </div>
                <CommentRichTextView content={comment.content ?? ''} taskId={taskId} />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
